import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import MapPage from "./components/MapPage";
import Dashboard from "./components/Dashboard";
import ResetPasswordPage from "./components/ResetPasswordPage";
import VerifyEmailPage from "./components/VerifyEmailPage";
import UsernameRecoveryPage from "./components/UsernameRecoveryPage";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { BusinessAuthProvider } from "./contexts/BusinessAuthContext";
import AuthModal from "./components/AuthModal";
import { useAuth } from "./contexts/AuthContext";
import PWAInstallBanner from "./components/PWAInstallBanner";
import BottomNav from "./components/BottomNav";
// DelightOverlay - Can be toggled on/off by master admin
import DelightOverlay from "./components/DelightOverlay";
import { BACKEND_URL, API_BASE, DEFAULT_TIMEOUT } from "./config/api";
import ErrorBoundary from "./components/ErrorBoundary";
import errorReporter from "./utils/errorReporter";
import ErrorMonitoringDashboard from "./components/ErrorMonitoringDashboard";
import AdminPanel from "./components/AdminPanel";
import BusinessLogin from "./components/BusinessLogin";
import BusinessAcceptInvitation from "./components/BusinessAcceptInvitation";
import BusinessDashboard from "./components/BusinessDashboard";
import ProtectedBusinessRoute from "./components/ProtectedBusinessRoute";

// Configure axios defaults globally for all API calls
axios.defaults.baseURL = API_BASE;
axios.defaults.timeout = DEFAULT_TIMEOUT;

// Add axios interceptor for error reporting
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Report API errors to error tracking
    if (error.response && error.response.status >= 500) {
      errorReporter.captureAPIError(
        error,
        error.config?.url || 'unknown',
        error.config?.method?.toUpperCase() || 'GET'
      );
    }
    return Promise.reject(error);
  }
);

// Animated word flip component
const FlipWords = () => {
  const words = ['Everything', 'Your', 'Dog', 'Needs'];
  const toys = [
    { emoji: '🎾', name: 'Ball' },
    { emoji: '🦴', name: 'Bone' },
    { emoji: '🪢', name: 'Rope' },
    { emoji: '🥏', name: 'Frisbee' }
  ];
  
  const [flippedIndices, setFlippedIndices] = useState([]);
  
  useEffect(() => {
    words.forEach((_, index) => {
      setTimeout(() => {
        setFlippedIndices(prev => [...prev, index]);
      }, 3000 + (index * 500)); // Start flipping after 3s, then 500ms apart
    });
  }, []);
  
  return (
    <span className="inline-flex flex-wrap justify-center gap-3">
      {words.map((word, index) => (
        <span
          key={index}
          className={`inline-block transition-all duration-700 transform ${
            flippedIndices.includes(index) 
              ? 'rotate-y-180 scale-110' 
              : ''
          }`}
          style={{
            transformStyle: 'preserve-3d',
            perspective: '1000px'
          }}
        >
          {flippedIndices.includes(index) ? (
            <span className="inline-flex flex-col items-center">
              <span className="text-5xl mb-1">{toys[index].emoji}</span>
              <span className="text-lg text-emerald-600">{toys[index].name}</span>
            </span>
          ) : (
            word
          )}
        </span>
      ))}
    </span>
  );
};

const LogoutButton = () => {
  const { user, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowLogoutModal(true)}
        className="group bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white w-12 h-12 rounded-full font-semibold shadow-lg hover:shadow-red-500/50 transition-all duration-300 transform hover:scale-110 flex items-center justify-center"
        title="Logout"
      >
        <span className="text-2xl">🚪</span>
      </button>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-4">🚪</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Confirm Logout</h3>
              <p className="text-gray-600 mb-6">Are you sure you want to logout?</p>
              
              {/* Remember Me Option */}
              <div className="flex items-center justify-center mb-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Remember me next time I open the app</span>
                </label>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (rememberMe) {
                      localStorage.setItem('rememberMe', 'true');
                      localStorage.setItem('lastUsername', user?.username || '');
                      const lastPassword = localStorage.getItem('tempPassword') || '';
                      localStorage.setItem('lastPassword', lastPassword);
                    } else {
                      localStorage.removeItem('rememberMe');
                      localStorage.removeItem('lastUsername');
                      localStorage.removeItem('lastPassword');
                    }
                    setShowLogoutModal(false);
                    logout();
                  }}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showBouncingBall, setShowBouncingBall] = useState(false);
  const [showBusinessLoginModal, setShowBusinessLoginModal] = useState(false);
  const { isAuthenticated, user } = useAuth();

  const testConnection = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/`);
      console.log('✅ API Connection:', response.data.message);
    } catch (error) {
      console.error('❌ API Connection failed:', error);
      // Don't block the app if connection test fails
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    testConnection();
    
    // Check if this is the first session and show bouncing ball
    const hasSeenBouncingBall = sessionStorage.getItem('hasSeenBouncingBall');
    if (!hasSeenBouncingBall) {
      setShowBouncingBall(true);
      sessionStorage.setItem('hasSeenBouncingBall', 'true');
      
      // Hide the ball after 5 seconds
      setTimeout(() => {
        setShowBouncingBall(false);
      }, 5000);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Hero Section */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Top Right Auth Button - Compact Round Design */}
        <div className="absolute top-6 right-6 z-20">
          {!isAuthenticated ? (
            <AuthModal defaultTab="register">
              <button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-12 h-12 rounded-full font-semibold shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 hover:scale-110 flex items-center justify-center"
                title="Login / Register"
              >
                <span className="text-2xl">👤</span>
              </button>
            </AuthModal>
          ) : (
            <LogoutButton />
          )}
        </div>
        
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/34060703/pexels-photo-34060703.jpeg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center justify-center mb-6">
              {/* Single Happy Paws paw print icon - BTN_002 orange (#f97316) */}
              <svg className="w-24 h-24" width="96" height="96" viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg">
                {/* White circle background with green border */}
                <circle cx="48" cy="48" r="46" fill="white" stroke="#10B981" strokeWidth="4"/>
                {/* Frameless paw print - BTN_002 orange for cross-site consistency */}
                <image href="/icons/paw-frameless-64.png" x="16" y="16" width="64" height="64" />
              </svg>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                Happy Paws
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto leading-relaxed">
              Discover the perfect dog walking spots in your neighborhood. Find parks, rate locations, and connect with fellow dog lovers.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* Explore Paw Parks */}
            <button
              onClick={() => window.location.href = '/map'}
              className="group bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-4 rounded-full font-semibold text-base md:text-lg shadow-2xl hover:shadow-emerald-500/25 transition-all duration-300 transform hover:scale-105 max-w-[240px] w-full mx-auto"
              data-testid="explore-map-button"
            >
              <div className="flex items-center w-full">
                <div className="flex-shrink-0 w-12 flex justify-center">
                  <span className="text-xl">🗺️</span>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center leading-tight">
                    <div className="text-base">Explore</div>
                    <div className="text-base">Paw Parks</div>
                  </div>
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>

            {/* Dashboard */}
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="group bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-6 py-4 rounded-full font-semibold text-base md:text-lg shadow-2xl hover:shadow-amber-500/25 transition-all duration-300 transform hover:scale-105 max-w-[240px] w-full"
              data-testid="dashboard-button"
            >
              <div className="flex items-center w-full">
                <div className="flex-shrink-0 w-12 flex justify-center">
                  <span className="text-xl">{isAuthenticated ? `👋` : `📊`}</span>
                </div>
                <div className="flex-1 flex justify-center">
                  <span>Dashboard</span>
                </div>
              </div>
            </button>

            {/* Business Portal Login */}
            <button
              onClick={() => setShowBusinessLoginModal(true)}
              className="group bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-4 rounded-full font-semibold text-base md:text-lg shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 transform hover:scale-105 max-w-[240px] w-full"
              data-testid="business-login-button"
            >
              <div className="flex items-center w-full">
                <div className="flex-shrink-0 w-12 flex justify-center">
                  <span className="text-xl">💼</span>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center leading-tight">
                    <div className="text-base">Business</div>
                    <div className="text-base">Portal</div>
                  </div>
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          </div>

          {/* Integrated login into main buttons above */}

          {isAuthenticated && (
            <div className="mt-8 pt-6 border-t border-white/20">
              <p className="text-emerald-300 mb-2">
                Welcome back, <span className="font-semibold">{user?.username}</span>! 
                {user?.is_verified ? ' ✅' : ' 📧 (Verify email to add locations)'}
              </p>
            </div>
          )}
        </div>
        
        {/* PWA Install Banner */}
        <PWAInstallBanner />
        
        {/* Bottom Navigation for Mobile */}
        <BottomNav />
        
        {/* Orange Bouncing Ball Animation (Once per session) */}
        {showBouncingBall && (
          <div className="absolute inset-0 pointer-events-none z-20">
            <div 
              className="absolute"
              style={{
                left: '20%',
                top: '30%',
                animation: 'bouncingBall 2s ease-in-out infinite'
              }}
            >
              <div 
                className="w-16 h-16 rounded-full shadow-2xl"
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)', // Red gradient matching logout button
                  boxShadow: '0 8px 32px rgba(239, 68, 68, 0.4)'
                }}
              >
                {/* Ball highlight */}
                <div 
                  className="w-4 h-4 bg-white opacity-60 rounded-full absolute"
                  style={{ top: '15%', left: '25%' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              <FlipWords />
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From finding nearby parks to connecting with the community, Happy Paws makes dog walking an adventure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Find Parks Near You</h3>
              <p className="text-gray-600 leading-relaxed">
                Use your location to discover amazing dog-friendly parks and walking trails in your area with our interactive map.
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Rate & Review</h3>
              <p className="text-gray-600 leading-relaxed">
                Share your experiences and read reviews from other dog owners to find the best spots for your furry friend.
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Community Driven</h3>
              <p className="text-gray-600 leading-relaxed">
                Join a community of dog lovers. Add new locations, share photos, and help other pet parents find great spots.
              </p>
            </div>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => window.location.href = '/map'}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-emerald-500/25 transition-all duration-300 transform hover:scale-105"
              data-testid="get-started-button"
            >
              Get Started Now 🚀
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="mb-6">
            <h3 className="text-2xl font-bold mb-2">Happy Paws</h3>
            <p className="text-gray-400">Making every walk an adventure</p>
          </div>
          <div className="border-t border-gray-700 pt-6">
            <p className="text-gray-400">
              © 2024 Happy Paws. Built with ❤️ for dog lovers everywhere.
            </p>
          </div>
        </div>
      </footer>
      
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
            <span className="text-gray-700">Connecting to API...</span>
          </div>
        </div>
      )}

      {/* Business Login Modal */}
      {showBusinessLoginModal && (
        <BusinessLogin 
          isModal={true} 
          onClose={() => setShowBusinessLoginModal(false)} 
        />
      )}
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BusinessAuthProvider>
          <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/username-recovery" element={<UsernameRecoveryPage />} />
              <Route path="/admin/errors" element={<ErrorMonitoringDashboard />} />
              <Route path="/admin" element={<AdminPanel />} />
              
              {/* Business Portal Routes */}
              <Route path="/business-login" element={<BusinessLogin />} />
              <Route path="/business-accept-invitation" element={<BusinessAcceptInvitation />} />
              <Route 
                path="/business-portal" 
                element={
                  <ProtectedBusinessRoute>
                    <BusinessDashboard />
                  </ProtectedBusinessRoute>
                } 
              />
            </Routes>
          <Toaster 
            position="bottom-right"
            duration={2000}
            visibleToasts={2}
            toastOptions={{
              style: {
                marginBottom: '20px'
              }
            }}
          />
          {/* The Invisible Net - Universal delight layer */}
          {/* Master admin can toggle this on/off via Admin Panel > Admin Info */}
          {localStorage.getItem('delightOverlayEnabled') === 'true' && <DelightOverlay />}
        </BrowserRouter>
      </div>
        </BusinessAuthProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;